import React, { useCallback, useState, useEffect, useRef } from 'react';
import Button from '../Button';
import IconAsButton from '../IconAsButton';
import CircularProgress from '../CircularProgress';
import styled from 'styled-components';
import Typo from '../Typo';
import theme from '../theme';
import { toRgba } from '../theme/colors';

// New styled component for the overlay
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${toRgba(theme.colors.NEU_0, 0.7)};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const Container = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  position: relative;
`;

const Downloadable = ({ file, variant = 'button', label, icon, children, ...props }) => {
  const [loadedValue, setLoadedValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);
  const isOpenVariant = variant === 'open';

  const countLoadedValue = useCallback((loaded, total) => {
    if (total > 0) {
      setLoadedValue(((loaded / total) * 100).toFixed());
    }
  }, []);

  const onFileClick = useCallback(async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(`${file?.url}?${new Date().getTime()}`, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch the file');
      }

      const reader = response.body.getReader();
      const contentLength = +response.headers.get('Content-Length') || 0;
      let receivedLength = 0;
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        countLoadedValue(receivedLength, contentLength);
      }

      const blob = new Blob(chunks, { type: response.headers.get('Content-Type') || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.target = '_blank';
      a.rel = 'noreferrer';
      a.download = file?.name || 'file';
      a.click();

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setError('Failed to download the file. Please try again.');
      }
    } finally {
      setIsLoading(false);
      setLoadedValue(0);
    }
  }, [file, isLoading, countLoadedValue]);

  const onOpenFile = useCallback(() => {
    window.open(file.url, '_blank', 'noopener,noreferrer');
  }, [file]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle keyboard accessibility for Container
  // const handleKeyDown = useCallback((e) => {
  //   if (e.key === 'Enter' || e.key === ' ') {
  //     e.preventDefault();
  //     onFileClick();
  //   }
  // }, [onFileClick]);

  const buttonProps = {
    isLoading,
    progressValue: loadedValue,
    onClick: isOpenVariant ? onOpenFile : onFileClick,
    'aria-label': `${isOpenVariant ? 'Open' : 'Download'} ${file?.name || 'file'}`,
    ...props,
  };

  // Render based on variant
  const renderContent = () => {
    if (variant === 'component') {
      return (
        <>
          {children}
          {isLoading && (
            <LoadingOverlay aria-hidden={!isLoading}>
              <CircularProgress value={loadedValue} />
            </LoadingOverlay>
          )}
        </>
      );
    }

    return (
      <Button
        {...buttonProps}
        icon={icon || (variant === 'attach' ? 'AttachFile' : variant === 'button' ? 'Download' : undefined)}
        $asText={variant === 'text' || variant === 'attach'}
      >
        {label || (variant === 'text' || variant === 'attach' ? file?.name : isOpenVariant ? file?.name || 'Open' : 'Download')}
      </Button>
    );
  };

  return variant === 'icon' ? (
    <IconAsButton {...props} loading={isLoading} progressValue={loadedValue} clickHandle={isOpenVariant ? onOpenFile : onFileClick} />
  ) : (
    <Container
      onClick={variant === 'component' ? (isOpenVariant ? onOpenFile : onFileClick) : undefined}
      // onKeyDown={variant === 'component' ? handleKeyDown : undefined}
      tabIndex={variant === 'component' ? 0 : undefined}
      role={variant === 'component' ? 'button' : undefined}
      aria-busy={isLoading}
      aria-label={
        isLoading
          ? `Downloading ${file?.name || 'file'} (${loadedValue}%)`
          : `${isOpenVariant ? 'Open' : 'Download'} ${file?.name || 'file'}`
      }
    >
      {renderContent()}
      {error && <Typo.Info color={theme.colors.ERROR_RED}>{error}</Typo.Info>}
    </Container>
  );
};

// Export variants
const DownloadableFile = {
  AsIcon: (props) => <Downloadable variant='icon' file={props.file} {...props} />,
  AsAttach: (props) => <Downloadable variant='attach' file={props.file} {...props} />,
  AsButton: (props) => <Downloadable variant='button' file={props.file} {...props} />,
  AsComponent: (props) => <Downloadable variant='component' file={props.file} {...props} />,
  AsText: (props) => <Downloadable variant='text' file={props.file} label={props.label} {...props} />,
};

export default DownloadableFile;
